/**
 * Footer for the agntInc tenant — the shared chrome rendered by `AgntIncLayout`
 * on every AGNT page. Ported from the redesign mock's `<footer>`: an "AGNT
 * Companies" brand strip, a four-column grid (brand mark + description + social,
 * then Company / Investors / Get Started link columns), and a bottom legal bar.
 *
 * Link/content data lives in `./content`; the brand mark reuses the shared
 * `AgntLogo`. Internal AGNT routes render through next/link; company sites,
 * legal, and social links open in a new tab.
 */

import Link from 'next/link';

import AgntLogo from 'components/agnt-inc/agnt-logo';

import { FOOTER_CONTENT } from './content';
import styles from './style.module.scss';

import type { FooterLink, SocialIcon } from './content';

const SOCIAL_ICONS: Record<SocialIcon, JSX.Element> = {
  facebook: <path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.5 2.9h-2.4v6.9A10 10 0 0 0 22 12Z" />,
  x: <path d="M18.2 2H21l-7.1 8.2L22.4 22h-6.5l-5.1-6.7L4.8 22H2l7.6-8.8L1.7 2h6.7l4.6 6 5.2-6Zm-2.3 18.5h1.6L6.5 3.4H4.8l11.1 17.1Z" />,
  linkedin: <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM8.3 18.3H5.7V9.7h2.6v8.6ZM7 8.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm11.3 9.8h-2.6v-4.2c0-1 0-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.3h-2.6V9.7h2.5v1.2a2.7 2.7 0 0 1 2.5-1.4c2.7 0 3.2 1.8 3.2 4v4.8Z" />,
  instagram: <path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2 0 1.8.2 2.2.4.6.2 1 .5 1.4 1 .5.4.8.8 1 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c0 1.2-.2 1.8-.4 2.2-.2.6-.5 1-1 1.4-.4.5-.8.8-1.4 1-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2 0-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-1-.5-.4-.8-.8-1-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c0-1.2.2-1.8.4-2.2.2-.6.5-1 1-1.4.4-.5.8-.8 1.4-1C5.5 2.3 6.3 2.1 7.5 2.1 8.7 2 9.1 2 12 2Zm0 5.8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm6.4-2.4a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9Z" />,
};

// Internal app routes (`/…`) render through next/link; everything else (http,
// mailto, `#alerts` on-page anchor) is a plain anchor — http opens a new tab.
function FooterAnchor({ link, className }: { link: FooterLink; className?: string }) {
  const { href, label } = link;
  if (href.startsWith('/')) {
    return <Link href={href} className={className}>{label}</Link>;
  }
  const isHttp = /^https?:\/\//i.test(href);
  return (
    <a href={href} className={className} {...(isHttp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {label}
    </a>
  );
}

export default function AgntIncFooter() {
  const { brands, description, social, columns, legal } = FOOTER_CONTENT;
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.brands}>
        <div className={styles.brandsIn}>
          <span className={styles.brandsLabel}>AGNT Companies</span>
          {brands.map(brand => (
            <FooterAnchor key={brand.label} link={brand} />
          ))}
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.intro}>
          <Link href="/" aria-label="AGNT, Inc. home" className={styles.logoLink}>
            <AgntLogo className={styles.logo} />
          </Link>
          <p className={styles.desc}>{description}</p>
          <div className={styles.social}>
            {social.map(item => (
              <a
                key={item.icon}
                href={item.href}
                className={styles.socialLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  {SOCIAL_ICONS[item.icon]}
                </svg>
              </a>
            ))}
          </div>
        </div>

        {columns.map(column => (
          <div key={column.heading} className={styles.col}>
            <h4 className={styles.colHeading}>{column.heading}</h4>
            <ul className={styles.colList}>
              {column.links.map(link => (
                <li key={link.label}>
                  <FooterAnchor link={link} className={styles.colLink} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className={styles.bottom}>
        <p className={styles.copyright}>© {year} AGNT, Inc. (Nasdaq: AGNT) All Rights Reserved.</p>
        <div className={styles.legal}>
          {legal.map(link => (
            <FooterAnchor key={link.label} link={link} className={styles.legalLink} />
          ))}
        </div>
      </div>
    </footer>
  );
}
